import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api, { getImageUrl } from '../../utils/api';
import Loader from '../../components/common/Loader';
import BadgeStatus from '../../components/common/BadgeStatus';
import TransferForm from '../../components/assets/TransferForm';
import '../../styles/Forms.css';

const EmployeeTransferFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    transferDate: new Date().toISOString().split('T')[0],
    transferTo: '',
    notes: '',
  });

  useEffect(() => {
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
    }

    // Always fetch employees/users list
    fetchEmployees();

    // Get selected assets from location state
    if (location.state && location.state.selectedAssets) {
      setSelectedAssets(location.state.selectedAssets);
    }
  }, [location.state, location.pathname]);

  const fetchEmployees = async () => {
    try {
      // Fetch all users (employees + admins) for transfer
      const response = await api.getUsers({ isActive: 'true', limit: 10000 });
      if (response.success) {
        const userList = Array.isArray(response.data) ? response.data : [];
        // Filter out current user from the list
        const userStr = sessionStorage.getItem('user');
        let currentUserId = null;
        if (userStr) {
          const user = JSON.parse(userStr);
          currentUserId = user._id || user.id;
        }
        const filteredUsers = userList.filter(user => {
          const userId = user._id || user.id;
          return userId?.toString() !== currentUserId?.toString();
        });
        setEmployees(filteredUsers);
      } else {
        console.error('Failed to fetch users:', response.message);
        toast.error('Failed to load users list');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error loading users');
    }
  };

  const handleRemoveAsset = (assetId) => {
    setSelectedAssets(selectedAssets.filter(asset => (asset.id || asset._id) !== assetId));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.transferTo) {
      toast.error('Please select a user to transfer to');
      return;
    }

    if (selectedAssets.length === 0) {
      toast.error('No assets selected');
      return;
    }

    try {
      setSubmitting(true);
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Transfer each asset with skipEmail flag
      const transferResults = [];
      for (const asset of selectedAssets) {
        try {
          const assetId = asset.id || asset._id;
          const response = await api.transferAsset(assetId, {
            transferredTo: formData.transferTo,
            notes: formData.notes,
            transferDate: new Date().toISOString().split('T')[0], // Always use current date
            skipEmail: true, // Skip individual emails, will send consolidated email
          });

          if (response.success) {
            successCount++;
            // Store transfer info for consolidated email
            transferResults.push({
              assetId: assetId,
              handoverId: response.handover?._id || response.handover?.id,
              transferredTo: formData.transferTo,
            });
          } else {
            errorCount++;
            errors.push(`${asset.assetTag || 'Asset'}: ${response.message || 'Transfer failed'}`);
          }
        } catch (error) {
          errorCount++;
          const assetTag = asset.assetTag || 'Asset';
          errors.push(`${assetTag}: ${error.response?.data?.message || 'Error transferring asset'}`);
        }
      }
      
      // Send consolidated transfer email after all transfers complete
      if (transferResults.length > 0) {
        try {
          await api.sendBulkTransferEmail({
            transfers: transferResults,
          });
        } catch (error) {
          console.error('Error sending consolidated transfer email:', error);
          // Don't fail the transfer if email fails
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} asset${successCount !== 1 ? 's' : ''} transferred successfully! Awaiting recipient acknowledgment.`);
      }
      
      if (errorCount > 0) {
        toast.error(`${errorCount} asset${errorCount !== 1 ? 's' : ''} failed to transfer. ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`);
      }

      setTimeout(() => {
        // Determine the base path based on current location
        const isAdmin = location.pathname.startsWith('/admin');
        const myAssetsPath = isAdmin ? '/admin/my-assets' : '/employee/my-assets';
        navigate(myAssetsPath);
      }, 1500);
    } catch (error) {
      console.error('Error transferring assets:', error);
      toast.error('Error transferring assets');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Always show something - even if empty, show the form structure
  if (loading && selectedAssets.length === 0) {
    return <Loader />;
  }

  // Show form even if no assets - let user see the form structure
  // Only show "No Assets" message if we're sure there are no assets after checking state
  if (selectedAssets.length === 0 && (!location.state || !location.state.selectedAssets)) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{
          background: 'white',
          padding: '60px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#2d3748' }}>
            No Assets Selected
          </h3>
          <p style={{ margin: 0, color: '#718096', fontSize: '13px', marginBottom: '20px' }}>
            Please go back and select assets for transfer
          </p>
          <button
            onClick={() => {
              // Determine the base path based on current location
              const isAdmin = location.pathname.startsWith('/admin');
              const myAssetsPath = isAdmin ? '/admin/my-assets' : '/employee/my-assets';
              navigate(myAssetsPath);
            }}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '13px'
            }}
          >
            Go to My Assets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="transfer-form-page" style={{ padding: '16px' }}>
      <h1 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>Transfer Assets</h1>
      {/* Assets Pending Section */}
      <div style={{
        background: 'white',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
          Assets to Transfer ({selectedAssets.length})
        </h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#f7fafc' }}>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Image</th>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Asset Tag</th>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Description</th>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {selectedAssets.map((asset) => {
                const assetId = asset.id || asset._id;
                return (
                  <tr key={assetId} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '10px' }}>
                      {asset.photos && asset.photos.length > 0 ? (
                        <img
                          src={getImageUrl(asset.photos[0].url)}
                          alt={asset.description}
                          style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div style={{ width: '40px', height: '40px', borderRadius: '4px', backgroundColor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#718096' }}>📦</div>
                      )}
                    </td>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                      {asset.assetTag || 'N/A'}
                    </td>
                    <td style={{ padding: '10px', color: '#4a5568', fontSize: '13px' }}>
                      {asset.description || 'N/A'}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <BadgeStatus status={asset.status || 'available'} />
                    </td>
                    <td style={{ padding: '10px' }}>
                      <button
                        onClick={() => handleRemoveAsset(assetId)}
                        style={{
                          padding: '4px 8px',
                          background: '#fed7d7',
                          color: '#c53030',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: 600
                        }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transfer Form Section */}
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
          {/* Left Column - Transfer Form */}
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Transfer Details</h2>
            <TransferForm
              formData={formData}
              employees={employees}
              currentUserId={currentUser?._id || currentUser?.id}
              onChange={handleFormChange}
            />

            {/* Submit Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                onClick={() => {
                  // Determine the base path based on current location
                  const isAdmin = location.pathname.startsWith('/admin');
                  const myAssetsPath = isAdmin ? '/admin/my-assets' : '/employee/my-assets';
                  navigate(myAssetsPath);
                }}
                style={{
                  padding: '10px 24px',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = '#cbd5e0'}
                onMouseLeave={(e) => e.target.style.background = '#e2e8f0'}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || selectedAssets.length === 0}
                style={{
                  padding: '10px 24px',
                  background: submitting || selectedAssets.length === 0 ? '#cbd5e0' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: submitting || selectedAssets.length === 0 ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!submitting && selectedAssets.length > 0) e.target.style.background = '#5568d3';
                }}
                onMouseLeave={(e) => {
                  if (!submitting && selectedAssets.length > 0) e.target.style.background = '#667eea';
                }}
              >
                {submitting ? `Transferring ${selectedAssets.length} Asset${selectedAssets.length !== 1 ? 's' : ''}...` : `Transfer ${selectedAssets.length} Asset${selectedAssets.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          {/* Right Column - Summary */}
          <div style={{
            background: 'white',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Transfer Summary</h2>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Number of Assets
                </label>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#2d3748' }}>
                  {selectedAssets.length}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                  Transfer Date
                </label>
                <div style={{ fontSize: '14px', color: '#4a5568' }}>
                  {formatDate(formData.transferDate)}
                </div>
              </div>
              {formData.transferTo && (
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#718096', marginBottom: '4px' }}>
                    Transfer To
                  </label>
                  <div style={{ fontSize: '14px', color: '#4a5568', fontWeight: 600 }}>
                    {employees.find(emp => (emp._id || emp.id) === formData.transferTo)?.name || 'Not selected'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EmployeeTransferFormPage;

